// File: src/pages/companies/CompanyDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Tabs,
  Button,
  Space,
  Tag,
  Descriptions,
  Table,
  Card,
  Empty,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  UserOutlined,
  ShopOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchCompanyById,
  editCompany,
  validateCompany,
} from "../../redux/company.slice";
import { fetchCompanyTypes } from "../../redux/companyType.slice";
import CompanyFormModal from "../../modals/peoples/CompaniesFormModal";
import LoadingState from "../../common/LoadingState";
import CommonFooter from "../../common/footer/commonFooter";
import StickyBar from "../../common/StickyBar";
import { useTranslation } from "react-i18next";
import SubscriptionsTab from "../subcriptions/SubscriptionsTab";

const fmtDate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "-");
const fmtDateTime = (d) => (d ? dayjs(d).format("DD/MM/YYYY HH:mm") : "-");

const CompanyDetail = () => {
  const { id, shopId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { company, loading } = useSelector((state) => state.company);
  const { companyTypes } = useSelector((state) => state.companyType);

  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchCompanyById(id));
      dispatch(fetchCompanyTypes());
    }
  }, [id, dispatch]);

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
  };

  const handleEditSubmit = (values) => {
    dispatch(editCompany({ id, company: values }))
      .unwrap()
      .then(() => {
        setShowEditModal(false);
        dispatch(fetchCompanyById(id));
      })
      .catch((error) => console.error("Error updating company:", error));
  };

  const handleValidate = () => {
    dispatch(validateCompany(id))
      .unwrap()
      .then(() => {
        message.success(
          t("companies.messages.validatedSuccessfully") ||
            "Company validated successfully",
        );
        dispatch(fetchCompanyById(id)); // Refresh data to show the updated "Validated" tag
      })
      .catch((error) => {
        message.error(
          t("companies.messages.validationError") || "Error validating company",
        );
        console.error("Error validating company:", error);
      });
  };

  const typeName =
    companyTypes?.find((ct) => ct.companyTypeId === company?.companyTypeId)
      ?.companyTypeName || "-";

  if (loading && !company) {
    return <LoadingState type="page" message={t("common.loadingData")} />;
  }

  if (!company) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Empty description={t("companies.detail.notFound")} />
          <Button onClick={() => navigate("/companies")}>
            {t("common.backToList")}
          </Button>
        </div>
      </div>
    );
  }

  // Tab 1: General Info
  const generalInfoTab = (
    <Card>
      <Descriptions bordered column={2}>
        <Descriptions.Item label={t("companies.modal.companyCode")}>
          {company.companyCode || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.companyName")}>
          {company.companyName || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.columns.phone")}>
          {company.companyPhone || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.columns.email")}>
          {company.companyEmail || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.taxNumber")}>
          {company.companyTaxNumber || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Fax">
          {company.companyFax || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.address")} span={2}>
          {company.companyAddress || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.columns.city")}>
          {company.companyTown || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.postalCode")}>
          {company.companyPostalCode || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.columns.country")}>
          {company.companyCountry || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.currency")}>
          {company.currencyName || company.companyCurrency || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.columns.type")}>
          {typeName}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.birthdate")}>
          {fmtDate(company.companyBirthdate)}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.capital")}>
          {company.companyCapital ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.employeesNumber")}>
          {company.companyEmployeesNumber ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.columns.status")}>
          <Tag color={company.companyIsActive ? "success" : "default"}>
            {company.companyIsActive
              ? t("common.active")
              : t("common.inactive")}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.columns.validated")}>
          <Tag color={company.companyIsValidated ? "processing" : "default"}>
            {company.companyIsValidated ? t("common.yes") : t("common.no")}
          </Tag>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );

  // Tab 2: License & Trial
  const licenseTrialTab = (
    <Card>
      <Descriptions
        bordered
        column={2}
        title={t("companies.detail.licenseInfo")}
      >
        <Descriptions.Item label={t("companies.modal.licenseStart")}>
          {fmtDate(company.licenseStartDate)}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.licenseEnd")}>
          {fmtDate(company.licenseEndDate)}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.licenseMonthlyPaid")}>
          <Tag color={company.licenseIsMonthlyPayed ? "blue" : "default"}>
            {company.licenseIsMonthlyPayed ? t("common.yes") : t("common.no")}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.licenseYearlyPaid")}>
          <Tag color={company.licenseIsYearlyPayed ? "blue" : "default"}>
            {company.licenseIsYearlyPayed ? t("common.yes") : t("common.no")}
          </Tag>
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        bordered
        column={2}
        title={t("companies.detail.trialInfo")}
        style={{ marginTop: 24 }}
      >
        <Descriptions.Item label={t("companies.modal.inTrial")}>
          <Tag color={company.companyIsInTrialPeriod ? "orange" : "default"}>
            {company.companyIsInTrialPeriod ? t("common.yes") : t("common.no")}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.trialStart")}>
          {fmtDate(company.trialPeriodStartDate)}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.trialEnd")}>
          {fmtDate(company.trialPeriodEndDate)}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        bordered
        column={2}
        title={t("companies.detail.counters")}
        style={{ marginTop: 24 }}
      >
        <Descriptions.Item label={t("companies.columns.users")}>
          {company.usersNumber ?? 0}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.customersNumber")}>
          {company.customersNumber ?? 0}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.modal.pointsMonthly")}>
          {company.pointsMonthlyAmount ?? 0}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );

  // Tab 3: Users & Shops
  // Tab 3: Users & Shops
  const userColumns = [
    {
      title: t("companies.detail.userName"),
      key: "name",
      render: (_, record) => {
        // Backend uses snake_case: user_first_name, user_last_name
        return (
          `${record.user_first_name || ""} ${record.user_last_name || ""}`.trim() ||
          "-"
        );
      },
    },
    {
      title: t("companies.detail.userId"),
      dataIndex: "user_id",
      render: (v) => v || "-",
    },

    {
      title: t("companies.columns.status"),
      key: "status",
      render: (_, record) => {
        const isActive = record.user_is_active ?? true;
        return (
          <Tag color={isActive ? "success" : "default"}>
            {isActive ? t("common.active") : t("common.inactive")}
          </Tag>
        );
      },
    },
  ];

  const shopColumns = [
    {
      title: t("companies.detail.shopName"),
      dataIndex: "shopName",
      render: (v) => v || "-",
    },
    {
      title: t("companies.columns.city"),
      render: (_, record) =>
        [record.shopTown, record.shopCountry].filter(Boolean).join(", ") || "-",
    },
    {
      title: t("companies.columns.phone"),
      dataIndex: "shopPhone",
      render: (v) => v || "-",
    },
    {
      title: t("companies.columns.status"),
      dataIndex: "shopIsActive",
      render: (active) => (
        <Tag color={active ? "success" : "default"}>
          {active ? t("common.active") : t("common.inactive")}
        </Tag>
      ),
    },
  ];

  const usersShopsTab = (
    <div>
      <Card
        title={
          <>
            <UserOutlined /> {t("companies.detail.users")}
          </>
        }
        style={{ marginBottom: 16 }}
      >
        <Table
          columns={userColumns}
          dataSource={company.users || []}
          rowKey="userId"
          pagination={false}
          locale={{
            emptyText: <Empty description={t("companies.detail.noUsers")} />,
          }}
        />
      </Card>

      <Card
        title={
          <>
            <ShopOutlined /> {t("companies.detail.shops")}
          </>
        }
      >
        <Table
          columns={shopColumns}
          dataSource={company.shops || []}
          rowKey="shopId"
          pagination={false}
          locale={{
            emptyText: <Empty description={t("companies.detail.noShops")} />,
          }}
        />
      </Card>
    </div>
  );

  // Tab 4: History
  const historyTab = (
    <Card>
      <Descriptions bordered column={2}>
        <Descriptions.Item label={t("companies.detail.createdAt")}>
          {fmtDateTime(company.createdAt)}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.detail.createdBy")}>
          {company.creator
            ? `${company.creator.userFirstName || ""} ${company.creator.userLastName || ""}`.trim()
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.detail.updatedAt")}>
          {fmtDateTime(company.updatedAt)}
        </Descriptions.Item>
        <Descriptions.Item label={t("companies.detail.updatedBy")}>
          {company.updator
            ? `${company.updator.userFirstName || ""} ${company.updator.userLastName || ""}`.trim()
            : "-"}
        </Descriptions.Item>
      </Descriptions>

      <div
        style={{
          marginTop: 24,
          padding: 16,
          background: "#f5f5f5",
          borderRadius: 4,
        }}
      >
        <InfoCircleOutlined style={{ marginRight: 8 }} />
        {t("companies.detail.historyNote")}
      </div>
    </Card>
  );

  const tabItems = [
    {
      key: "general",
      label: (
        <span>
          <InfoCircleOutlined />
          {t("companies.detail.generalInfo")}
        </span>
      ),
      children: generalInfoTab,
    },
    {
      key: "license",
      label: (
        <span>
          <SafetyCertificateOutlined />
          {t("companies.detail.licenseTrial")}
        </span>
      ),
      children: licenseTrialTab,
    },
    {
      key: "usersShops",
      label: (
        <span>
          <UserOutlined />
          {t("companies.detail.usersShops")}
        </span>
      ),
      children: usersShopsTab,
    },
    {
      key: "history",
      label: (
        <span>
          <HistoryOutlined />
          {t("companies.detail.history")}
        </span>
      ),
      children: historyTab,
    },
    {
      key: "subscriptions",
      label: (
        <span>
          <SafetyCertificateOutlined />
          {t("subscriptions.tabTitle")}
        </span>
      ),
      children: <SubscriptionsTab companyId={id} />,
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <StickyBar
            title={
              <Space>
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(`/${shopId}/companies`)}
                />
                <span>{company.companyName}</span>
                <Tag color={company.companyIsActive ? "success" : "default"}>
                  {company.companyIsActive
                    ? t("common.active")
                    : t("common.inactive")}
                </Tag>
                {company.companyIsValidated && (
                  <Tag color="processing">
                    {t("companies.columns.validated")}
                  </Tag>
                )}
              </Space>
            }
            actions={
              <Space>
                {!company.companyIsValidated && (
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleValidate}
                  >
                    {t("common.validate") || "Validate Company"}
                  </Button>
                )}

                <Button icon={<EditOutlined />} onClick={handleEdit}>
                  {t("common.edit")}
                </Button>
              </Space>
            }
          />

          <div className="card">
            <div className="card-body">
              <Tabs defaultActiveKey="general" items={tabItems} />
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      <CompanyFormModal
        open={showEditModal}
        onCancel={handleEditCancel}
        onSubmit={handleEditSubmit}
        isEdit={true}
        isReadMode={false}
        initialData={company}
      />
    </div>
  );
};

export default CompanyDetail;
